'use client';

import React, { useMemo, type MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../components/text';
import { IconButton } from '../../../components/icon-button';
import { Checkbox } from '../../../components/checkbox';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  File01Icon,
  Folder01Icon,
  Globe02Icon,
} from '@terra/ui-icons';
import { EntityIcon } from '../../icons/EntityIcon';
import { useNumberFormatter } from '@terra/ui-formatters';
import { decodeUriComponentSafely } from '@terra/shared-utils';
import { archivedEntityRowCss } from '../../../components/archived-entity-row';
import { isGreyedDomainTreeNode } from '../utils/domain-tree-archived-roots';
import {
  getAncestorIds,
  isDescendantSelected,
  DomainTreeNodeType,
  type DomainTreeNode,
} from '../utils/domain-tree.utils';

const BASE_INDENT_REM = 0.75;
const INDENT_PER_LEVEL_REM = 0.5;

function indentToRem(level: number): string {
  return `${BASE_INDENT_REM + level * INDENT_PER_LEVEL_REM}rem`;
}

function getCountBadgeState(
  node: DomainTreeNode,
  nodeIdToCountMap?: Record<string, number>,
): {
  displayCount: number;
  shouldShowCountBadge: boolean;
} {
  const customCount = nodeIdToCountMap?.[node.id];
  const hasCustomCount = customCount !== undefined;
  const displayCount = hasCustomCount ? customCount : node.assetCount;

  if (hasCustomCount) {
    return {
      displayCount,
      shouldShowCountBadge: displayCount > 0,
    };
  }

  if (node.nodeType === DomainTreeNodeType.AppLevel) {
    return {
      displayCount,
      shouldShowCountBadge: displayCount > 0,
    };
  }

  const isLeafNode = !node.children?.length;

  return {
    displayCount,
    shouldShowCountBadge: isLeafNode ? displayCount > 1 : displayCount > 0,
  };
}

export type CustomTreeNodeProps = {
  node: DomainTreeNode;
  level: number;
  selectedDomains: ReadonlySet<string>;
  onToggleSelection: (nodeId: string, checked: boolean) => void;
  onToggleExpanded: (nodeId: string) => void;
  nodeIdToCountMap?: Record<string, number>;
  isNodeSelectable?: (node: DomainTreeNode) => boolean;
  selectionMode?: 'multiple' | 'single';
  cascadeSelectionState?: boolean;
  allNodesSelected?: boolean;
};
export function CustomTreeNode({
  node,
  level,
  selectedDomains,
  onToggleSelection,
  onToggleExpanded,
  nodeIdToCountMap,
  isNodeSelectable,
  selectionMode = 'multiple',
  cascadeSelectionState = false,
  allNodesSelected = false,
}: CustomTreeNodeProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const numberFormatter = useNumberFormatter();
  const isSelected =
    allNodesSelected ||
    selectedDomains.has(node.id) ||
    (cascadeSelectionState &&
      (getAncestorIds(node).some((id) => selectedDomains.has(id)) ||
        isDescendantSelected(node, selectedDomains)));
  const hasChildren = node.children && node.children.length > 0;
  const isSelectable = isNodeSelectable ? isNodeSelectable(node) : true;
  const isGreyed = isGreyedDomainTreeNode(node);
  const isIndeterminate = useMemo(() => {
    if (isSelected || !hasChildren) return false;

    return isDescendantSelected(node, selectedDomains);
  }, [isSelected, hasChildren, node, selectedDomains]);
  const paddingLeftRem = indentToRem(level);
  const getNodeIcon = () => {
    if (node.nodeType === DomainTreeNodeType.Endpoint) {
      return (
        <EntityIcon type="endpoint" appearance="default" density="compact" />
      );
    }

    if (node.nodeType === DomainTreeNodeType.Webpage) {
      return (
        <EntityIcon type="webPage" appearance="default" density="compact" />
      );
    }

    if (node.nodeType === DomainTreeNodeType.Path) {
      return hasChildren ? (
        <Folder01Icon size={14} />
      ) : (
        <File01Icon size={14} />
      );
    }

    if (node.nodeType === DomainTreeNodeType.AppLevel) {
      return <Folder01Icon size={14} />;
    }

    return <Globe02Icon size={14} />;
  };

  const getNodeFontWeight = (): 'semibold' | 'medium' | 'normal' => {
    const isSemibold =
      (selectionMode === 'single' && isSelected) ||
      node.nodeType === DomainTreeNodeType.Endpoint ||
      node.nodeType === DomainTreeNodeType.Webpage ||
      node.assets.length > 0;
    const isMedium =
      node.nodeType === DomainTreeNodeType.Path ||
      node.nodeType === DomainTreeNodeType.AppLevel ||
      level === 0;

    if (isSemibold) return 'semibold';
    if (isMedium) return 'medium';

    return 'normal';
  };

  const getNodeColor = (): string => {
    if (isGreyed) return 'fg.muted';
    if (!isSelectable) return 'fg.disabled';
    if (selectionMode === 'single' && isSelected)
      return 'colorPalette.emphasized';

    return 'fg.default';
  };

  const nodeTitle =
    node.nodeType === DomainTreeNodeType.AppLevel
      ? t('applicationLevel')
      : node.nodeType === DomainTreeNodeType.Endpoint ||
          node.nodeType === DomainTreeNodeType.Webpage ||
          node.nodeType === DomainTreeNodeType.Path
        ? `${node.fullDomain}${node.fullPath || ''}`
        : node.fullDomain;
  const { displayCount, shouldShowCountBadge } = getCountBadgeState(
    node,
    nodeIdToCountMap,
  );
  const onClickRow = (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    const isClickOnExpandOrCheckbox = Boolean(
      target.closest('button') ?? target.closest('[role="button"]'),
    );

    if (isClickOnExpandOrCheckbox) return;
    if (selectionMode === 'single' && hasChildren) {
      event.stopPropagation();
      onToggleExpanded(node.id);

      return;
    }

    if (!isSelectable) {
      return;
    }

    onToggleSelection(node.id, !isSelected);
  };

  return (
    <Box {...(isGreyed ? { ...archivedEntityRowCss, opacity: 0.58 } : {})}>
      <HStack
        gap={2}
        alignItems="center"
        py={0.5}
        pr={1}
        _hover={isSelectable ? { bg: 'bg.subtle' } : undefined}
        borderRadius="md"
        cursor={isSelectable || hasChildren ? 'pointer' : 'not-allowed'}
        onClick={onClickRow}
        minH="24px"
        style={{ paddingLeft: paddingLeftRem }}
      >
        <Box w="20px" display="flex" justifyContent="center" flexShrink={0}>
          {hasChildren ? (
            <IconButton
              variant="ghost"
              size="xs"
              aria-label={node.isExpanded ? 'collapse' : 'expand'}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpanded(node.id);
              }}
            >
              {node.isExpanded ? (
                <ArrowDown01Icon size={14} />
              ) : (
                <ArrowRight01Icon size={14} />
              )}
            </IconButton>
          ) : (
            <Box w="20px" />
          )}
        </Box>

        {selectionMode === 'multiple' && (
          <Checkbox
            colorPalette="terra"
            checked={isIndeterminate ? 'indeterminate' : isSelected}
            onCheckedChange={(details) =>
              onToggleSelection(node.id, !!details.checked)
            }
            disabled={!isSelectable}
            size="sm"
            onClick={(e) => e.stopPropagation()}
            flexShrink={0}
          />
        )}

        <Box
          display="flex"
          alignItems="center"
          flexShrink={0}
          color={isSelectable ? 'fg.muted' : 'fg.disabled'}
        >
          {getNodeIcon()}
        </Box>

        <HStack flex={1} gap={2} alignItems="center" minW={0}>
          <Text
            fontSize="xs"
            fontWeight={getNodeFontWeight()}
            color={getNodeColor()}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            title={nodeTitle}
            flex={1}
          >
            {decodeUriComponentSafely(node.name)}
          </Text>

          {shouldShowCountBadge ? (
            <Text
              fontSize="xs"
              color="fg.muted"
              bg="bg.muted"
              px={1.5}
              py={0.5}
              borderRadius="full"
              flexShrink={0}
              fontWeight="medium"
              minW="auto"
            >
              {numberFormatter.format(displayCount)}
            </Text>
          ) : null}
        </HStack>
      </HStack>
    </Box>
  );
}
